'use client';
import React, { useMemo } from 'react';
import { useScopeCalculations } from '../../calculatedAttributes/store';
import { ELIGIBILITY_TRANSLATIONS } from '../../components/Eligibility/i18n';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { isQuoteUnresolved } from '../../utils/travelInsurance/premiumsPending';
import { TRANSLATIONS } from './i18n';
import { useProductEligibilityState } from './ProductEligibilityProvider';
import { useProductFilterState } from './ProductFilterProvider';
import { useActiveProductSort } from './ProductSortProvider';

/** Which noun the count uses. The caller names it; this block has no vertical. */
export type ResultNoun = 'PERSONAL_LOAN' | 'PLAN' | 'CREDIT_CARD';

export type ResultsSummaryProps = {
  resultNoun: ResultNoun;
};

/**
 * The count and the applied sort, above the results (CMS-1871 R11 / FR-3.5).
 *
 * Both are suppressed until there is a result set, and again whenever a travel
 * quote has not produced a usable answer: TI plans arrive with the page and
 * only the premium is pending, so the count would otherwise stand over a
 * column of skeletons and name a sort the cards have not been put in yet
 * (CMS-1542 R12.3), and on a failed quote it would claim "No plans found"
 * beside the panel already reporting the failure.
 */
export const ResultsSummary: React.FC<ResultsSummaryProps> = ({
  resultNoun,
}) => {
  const { brand, lang } = useCmsClientContext();
  const { travelQuote: travelQuotesData } = useScopeCalculations();
  const t = getClientTranslations(TRANSLATIONS, { brand, lang });
  const { resultCount, results } = useProductFilterState();
  const activeOption = useActiveProductSort();
  const eligibility = useProductEligibilityState();
  const eligibilityT = getClientTranslations(ELIGIBILITY_TRANSLATIONS, {
    brand,
    lang,
  });

  /**
   * How many of the listed loans the reader meets the criteria for (CMS-1905
   * AC 6), once an income is entered — before that the line states the number
   * of loans and nothing more (AC 5c). One count for the page, over the same
   * filtered set as the number beside it. `hasIncome` is false wherever the
   * page offers no eligibility check, so every other vertical's line is
   * unchanged.
   */
  const eligibleCount = useMemo(
    () =>
      eligibility.hasIncome && results
        ? results.filter(
            (product) => eligibility.verdictFor(product).outcome === 'MEETS',
          ).length
        : undefined,
    [eligibility, results],
  );

  if (resultCount === undefined || isQuoteUnresolved(travelQuotesData))
    return null;

  // Keyed by the noun rather than branched on it: a new member of `ResultNoun`
  // is then a compile error here, not a page that quietly says "1 plan found"
  // about a credit card.
  const forms: Record<ResultNoun, { none: string; one: string; many: string }> =
    {
      PERSONAL_LOAN: {
        none: t.sort_and_filter_results_pl_none,
        one: t.sort_and_filter_results_pl_one,
        many: t.sort_and_filter_results_pl_many,
      },
      PLAN: {
        none: t.sort_and_filter_results_ti_none,
        one: t.sort_and_filter_results_ti_one,
        many: t.sort_and_filter_results_ti_many,
      },
      CREDIT_CARD: {
        none: t.sort_and_filter_results_cc_none,
        one: t.sort_and_filter_results_cc_one,
        many: t.sort_and_filter_results_cc_many,
      },
    };

  const form = forms[resultNoun];
  const count =
    resultCount === 0
      ? form.none
      : resultCount === 1
        ? form.one
        : form.many.replace('{count}', String(resultCount));

  return (
    <div
      data-results-summary=""
      className="flex flex-wrap items-baseline gap-x-4 gap-y-1"
    >
      <p className="mhg-alpha-typography-body-2 font-bold text-text/primary">
        {count}
        {eligibleCount !== undefined && (
          <span className="font-normal text-text/secondary">
            {' · '}
            {eligibilityT.eligibility_count_eligible.replace(
              '{count}',
              String(eligibleCount),
            )}
          </span>
        )}
      </p>
      {activeOption?.label && (
        <p className="mhg-alpha-typography-body-2 text-text/secondary">
          {t.sort_and_filter_sorted_by.replace('{name}', activeOption.label)}
        </p>
      )}
    </div>
  );
};
